/**
 * PropertyContactCard Component
 * 
 * Core component for property contact/inquiry card
 */

'use client';

import { Phone, EnvelopeSimple, WhatsappLogo, ShareNetwork, CalendarBlank, FileArrowDown } from '@phosphor-icons/react/dist/ssr';
import { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { PropertyEnquiryModal } from './PropertyEnquiryModal';
import { AgentMeetingModal } from '../agents/AgentMeetingModal';
import { WishlistButton } from '@/components/customer/WishlistButton';
import { useTenantOptional } from '@/contexts/TenantContext';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

export interface PropertyContactCardProps {
  propertyId: string;
  propertyName: string;
  /** Property price — passed as conversion value to analytics */
  propertyPrice?: number | null;
  contactInfo?: {
    phone?: string;
    email?: string;
    whatsapp?: string;
  };
  /** Listing agent data from Route Royal — enables "Schedule Meeting" */
  listingAgent?: {
    id: string;
    name: string;
    email: string;
    avatar: string | null;
    jobTitle: string | null;
  } | null;
  messages: {
    contact: {
      title: string;
      subtitle: string;
      requestInfo: string;
      callUs: string;
      emailUs: string;
      whatsapp: string;
      downloadBrochure: string;
      shareProperty: string;
      saveProperty: string;
      saved: string;
      scheduleMeeting?: string;
    };
  };
  className?: string;
  /** Auth URL for unauthenticated wishlist clicks (e.g. locale-prefixed register path) */
  authUrl?: string;
  /** Document ID of the property brochure — enables gated "Download Brochure" button */
  brochureDocumentId?: string | undefined;
  /** Organisation ID needed for brochure download proxy */
  organisationId?: string | undefined;
}

const PLACEHOLDER_AVATAR =
  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=400&h=400';

export function PropertyContactCard({
  propertyId,
  propertyName,
  propertyPrice,
  contactInfo = {},
  listingAgent,
  messages,
  className = '',
  authUrl,
  brochureDocumentId,
  organisationId,
}: PropertyContactCardProps) {
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [brochureIntent, setBrochureIntent] = useState(false);
  const tenant = useTenantOptional();

  const handleShare = () => {
    analyticsEvents.shareContent('property', propertyId, 'share_button');
    if (navigator.share) {
      navigator.share({
        title: propertyName,
        url: window.location.href,
      }).catch(() => {
        navigator.clipboard.writeText(window.location.href);
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const handleRequestInfo = () => {
    analyticsEvents.contactProperty(propertyId, propertyName, 'request_info');
    setBrochureIntent(false);
    setShowEnquiryModal(true);
  };

  const handleDownloadBrochure = () => {
    analyticsEvents.contactProperty(propertyId, propertyName, 'download_brochure');
    setBrochureIntent(true);
    setShowEnquiryModal(true);
  };

  return (
    <div className={`bg-white rounded-2xl border border-[#E5E7EB] shadow-xl p-7 relative overflow-hidden font-inter ${className}`}>
      {/* Subtle gold accent corner */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#BBA339]/10 to-transparent rounded-bl-full"></div>
      
      {/* Header with gradient accent */}
      <div className="mb-6 pb-6 border-b border-[#F3F4F6] relative">
        <div className="flex items-start gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#243462] to-[#1a2647] flex items-center justify-center flex-shrink-0 ring-2 ring-[#BBA339]/20">
            <EnvelopeSimple size={20} weight="bold" className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-[#222222] leading-tight font-inter">
              {messages.contact.title}
            </h3>
            <p className="text-sm text-[#717171] mt-1 leading-relaxed">
              {messages.contact.subtitle}
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-16 h-0.5 bg-gradient-to-r from-[#BBA339] to-transparent"></div>
      </div>

      {/* Primary Actions */}
      <div className="space-y-3 mb-6">
        <button
          onClick={handleRequestInfo}
          className="group w-full bg-gradient-to-r from-[#243462] to-[#1a2647] text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
        >
          <EnvelopeSimple size={20} weight="bold" className="group-hover:scale-110 transition-transform" />
          <span>{messages.contact.requestInfo}</span>
        </button>

        {brochureDocumentId && (
          <button
            onClick={handleDownloadBrochure}
            className="group w-full border-2 border-[#BBA339] text-[#243462] py-4 px-6 rounded-xl font-semibold hover:bg-[#BBA339]/10 hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2"
          >
            <FileArrowDown size={20} weight="bold" className="text-[#BBA339] group-hover:scale-110 transition-transform" />
            <span>{messages.contact.downloadBrochure}</span>
          </button>
        )}
      </div>

      {/* Contact Methods */}
      {(contactInfo.phone || contactInfo.email || contactInfo.whatsapp) && (
        <>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#E5E7EB]"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">
                Or contact directly
              </span>
            </div>
          </div>
          
          <div className="space-y-2 mb-6">
            {contactInfo.phone && (
              <a
                href={`tel:${contactInfo.phone}`}
                onClick={() => analyticsEvents.clickToCall(contactInfo.phone!, `property_${propertyId}`)}
                className="flex items-center gap-3 text-[#222222] hover:bg-gradient-to-r hover:from-[#F7F7F7] hover:to-[#F3F4F6] transition-all p-3.5 rounded-xl group border border-transparent hover:border-[#E5E7EB]"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#243462]/10 to-[#243462]/5 flex items-center justify-center group-hover:scale-110 group-hover:shadow-md transition-all">
                  <Phone size={20} weight="bold" className="text-[#243462]" />
                </div>
                <div className="flex-1">
                  <span className="font-semibold text-sm block">{messages.contact.callUs}</span>
                  <span className="text-xs text-[#717171]">{contactInfo.phone}</span>
                </div>
              </a>
            )}
            
            {contactInfo.email && (
              <a
                href={`mailto:${contactInfo.email}`}
                onClick={() => analyticsEvents.clickToEmail(contactInfo.email!, `property_${propertyId}`)}
                className="flex items-center gap-3 text-[#222222] hover:bg-gradient-to-r hover:from-[#F7F7F7] hover:to-[#F3F4F6] transition-all p-3.5 rounded-xl group border border-transparent hover:border-[#E5E7EB]"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#243462]/10 to-[#243462]/5 flex items-center justify-center group-hover:scale-110 group-hover:shadow-md transition-all">
                  <EnvelopeSimple size={20} weight="bold" className="text-[#243462]" />
                </div>
                <div className="flex-1">
                  <span className="font-semibold text-sm block">{messages.contact.emailUs}</span>
                  <span className="text-xs text-[#717171] truncate block">{contactInfo.email}</span>
                </div>
              </a>
            )}
            
            {contactInfo.whatsapp && (
              <a
                href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi, I'm interested in ${propertyName}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analyticsEvents.clickToWhatsApp(contactInfo.whatsapp!, `property_${propertyId}`)}
                className="flex items-center gap-3 text-[#222222] hover:bg-gradient-to-r hover:from-green-50 hover:to-green-50/50 transition-all p-3.5 rounded-xl group border border-transparent hover:border-green-200"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center group-hover:scale-110 group-hover:shadow-md transition-all">
                  <WhatsappLogo size={20} weight="bold" className="text-green-600" />
                </div>
                <div className="flex-1">
                  <span className="font-semibold text-sm block">{messages.contact.whatsapp}</span>
                  <span className="text-xs text-[#717171]">Chat on WhatsApp</span>
                </div>
              </a>
            )}
          </div>
        </>
      )}

      {/* Schedule Meeting with Agent */}
      {listingAgent && (
        <div className="mb-6">
          <button
            onClick={() => {
              analyticsEvents.scheduleMeetingIntent(listingAgent.name, `property_${propertyId}`);
              setShowMeetingModal(true);
            }}
            className="group w-full border-2 border-violet-500 text-violet-600 py-4 px-6 rounded-xl font-semibold hover:bg-violet-500 hover:text-white hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2"
          >
            <CalendarBlank size={20} weight="bold" className="group-hover:scale-110 transition-transform" />
            <span>{messages.contact.scheduleMeeting ?? `Meet ${listingAgent.name}`}</span>
          </button>
        </div>
      )}

      {/* Secondary Actions */}
      <div className="pt-6 border-t border-[#F3F4F6]">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium text-sm border-2 border-[#E5E7EB] hover:border-[#243462] hover:bg-[#F7F7F7] text-[#222222] transition-all">
            <WishlistButton
              entityType="PROPERTY"
              entityId={propertyId}
              entityName={propertyName}
              value={propertyPrice ?? undefined}
              size="sm"
              variant="ghost"
              showLabel
              {...(authUrl ? { authUrl } : {})}
            />
          </div>
          
          <button
            onClick={handleShare}
            className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium text-sm border-2 border-[#E5E7EB] hover:border-[#243462] hover:bg-[#F7F7F7] text-[#222222] transition-all"
          >
            <ShareNetwork size={18} weight="bold" />
            <span className="hidden sm:inline">{messages.contact.shareProperty}</span>
          </button>
        </div>
      </div>

      {/* Property Enquiry Modal */}
      <AnimatePresence>
        {showEnquiryModal && (
          <PropertyEnquiryModal
            propertyId={propertyId}
            propertyName={propertyName}
            propertyPrice={propertyPrice ?? undefined}
            onClose={() => { setShowEnquiryModal(false); setBrochureIntent(false); }}
            brochureDocumentId={brochureIntent ? brochureDocumentId : undefined}
            organisationId={organisationId}
          />
        )}
      </AnimatePresence>

      {/* Agent Meeting Modal */}
      <AnimatePresence>
        {showMeetingModal && listingAgent && (
          <AgentMeetingModal
            agent={{
              id: listingAgent.id,
              name: listingAgent.name,
              image: listingAgent.avatar || PLACEHOLDER_AVATAR,
              email: listingAgent.email,
              ...(listingAgent.jobTitle ? { title: listingAgent.jobTitle } : {}),
            }}
            organisationId={tenant?.organisationId || ''}
            propertyPrice={propertyPrice ?? undefined}
            onClose={() => setShowMeetingModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
